import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button, Drawer, Menu, Checkbox, Tag, Badge, Empty, Spin, Image } from 'antd';
import { ClearOutlined, WarningOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import gfm from "remark-gfm";
import { isZh, ze } from 'utils/zhEn';
import { NoticeRecordCategory } from 'utils/constants/notice-record-category';
import reportEvent, { EVENT_ACTIONS } from 'utils/reportEvent';
import scss from './NoticeDrawer.scss';
import { Link } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import NoJumpNextPagination from 'components/antd-extension/NoJumpNextPagination.jsx';
import NoticeDrawerEcharts from './NoticeDrawerEcharts';
import IconCrashList from 'svg/v2/newcs_dashboard_left_crash_icon_normal.svg';
import IconAnrList from 'svg/v2/newcs_dashboard_left_anr_icon_normal.svg';
import IconErrorList from 'svg/v2/newcs_dashboard_left_error_icon_normal.svg';
import IconHangList from 'svg/v2/newcs_dashboard_left_hang_icon_normal.svg';
import IconOomList from 'svg/v2/newcs_dashboard_advancedsearch_allrecords_list_oom_icon.svg';
import MarkasreadNormal from 'svg/v2/newcs_dashboard_message_markasread_normal_icon.svg';
import MarkasunreadNormal from 'svg/v2/newcs_dashboard_message_markasunread_normal_icon.svg';
import { CS_STYLES } from 'utils/constants/style-constants';
import { sleep } from "utils/sleep";
import { CsSelect } from 'components/antd-extension/CsSelect';
import { ExceptionCategory } from 'utils/exception-category';
import { getExceptionIconByType } from 'utils/exception-icon';
import { withRouter } from 'react-router-dom';
import useZuStore from 'store/zustand/zustand-store';

// 提取根据不同条件生成复杂内容过滤参数的逻辑到独立函数
const generateComplexContentFilter = (key) => {
  let categoryList = null;
  if (key === 'ADD') {
      categoryList = null;
  } else if (key === 'System') {
      categoryList = [
          NoticeRecordCategory.APP_PRECONFIGURED_CRASH_ALARM,
          NoticeRecordCategory.APP_PRECONFIGURED_ERROR_ALARM
      ];
  } else if (key === 'Custom') {
      categoryList = [
          NoticeRecordCategory.APP_CUSTOM_CRASH_ALARM,
          NoticeRecordCategory.APP_CUSTOM_ANR_ALARM,
          NoticeRecordCategory.APP_CUSTOM_ERROR_ALARM,
          NoticeRecordCategory.APP_CUSTOM_HANG_ALARM,
          NoticeRecordCategory.APP_CUSTOM_OOM_ALARM,
          NoticeRecordCategory.APP_CUSTOM_MIXED_ALARM,
          NoticeRecordCategory.APP_CUSTOM_ALARM_PUSH_FAILED,
      ];
  } else {
      categoryList = [
          NoticeRecordCategory.NEW_FEATURE,
          NoticeRecordCategory.OPTIMIZATION,
          NoticeRecordCategory.NEW_ITERATION,
      ];
  }
  return categoryList ? categoryList : null;
};

const NoticeDrawer = (prop) => {
  const {
    location,
    appId,
    pid,
    drawerOpen,
    drawerDisable,
    onChangeDrawerOpen,
    listNotices,
    getMarkAllAsRead,
    getChangeReadStatus,
    getListNotices,
    total,
    isSuper,
    showOomNoticeType,
  } = prop;

  // 当pathname改变的时候关闭drawer
  useEffect(() => {
    onChangeDrawerOpen(false);
  },[location?.pathname]);

  const items = [
    {
      label:`${ze('全部','All')}`,
      key:'ADD',
      value: '',
    },{
      label:`${ze('系统告警','System Alert')}`,
      key: 'System',
      value: 'APP',
    },{
      label:`${ze('自定义告警','Custom Alert')}`,
      key: 'Custom',
      value: 'APP',
    },{
      label:`${ze('公告','Notice')}`,
      key:'GLOBAL',
      value: 'GLOBAL',
    }
  ];

  const announcementTypeList = [
    {
      label:`${ze('功能上线','Feature Launch')}`,
      value:NoticeRecordCategory.NEW_FEATURE,
      color: "default",
      icon: null,
    },{
      label:`${ze('功能优化','Feature Optimization')}`,
      value:NoticeRecordCategory.OPTIMIZATION,
      color: "default",
      icon: null,
    },{
      label:`${ze('版本迭代','Version Update')}`,
      value:NoticeRecordCategory.NEW_ITERATION,
      color: "default",
      icon: null,
    },{
      label:`${ze('自定义告警 - 崩溃','Custom Crash')}`,
      value:NoticeRecordCategory.APP_CUSTOM_CRASH_ALARM,
      color:'red',
      icon: <IconCrashList width="14" height="14" viewBox="0 0 20 20" style={{color:'#FF5461'}} />,
    },{
      label:`${ze('自定义告警 - ANR','Custom ANR')}`,
      value:NoticeRecordCategory.APP_CUSTOM_ANR_ALARM,
      color:'orange',
      icon: <IconAnrList width="14" height="14" viewBox="0 0 20 20" style={{color:'#c4792f'}} />,
    },{
      label:`${ze('自定义告警 - 错误','Custom Error')}`,
      value:NoticeRecordCategory.APP_CUSTOM_ERROR_ALARM,
      color:'purple',
      icon: <IconErrorList  width="14" height="14" viewBox="0 0 20 20" style={{color:'#a452DD'}}/>,
    },{
      label:`${ze('自定义告警 - 卡死','Custom Hang')}`,
      value:NoticeRecordCategory.APP_CUSTOM_HANG_ALARM,
      color:'red',
      icon: <IconHangList width="14" height="14" viewBox="0 0 20 20" style={{color:'#e12000'}} />,
    },
    // 自定义告警 - OOM（仅 iOS + 开启 enableAlarmOomCategory 时可见，王者需求）
    ...(showOomNoticeType ? [{
      label:`${ze('自定义告警 - OOM','Custom OOM')}`,
      value:NoticeRecordCategory.APP_CUSTOM_OOM_ALARM,
      color:'blue',
      icon: <IconOomList width="14" height="14" viewBox="0 0 20 20" style={{color:'#6669fe'}} />,
    }] : []),
    {
      label:`${ze('自定义告警 - 多类型','Custom Mixed')}`,
      value:NoticeRecordCategory.APP_CUSTOM_MIXED_ALARM,
      color:'red',
      icon: <IconCrashList width="14" height="14" viewBox="0 0 20 20" style={{color:'#FF5461'}} />,
    },{
      label:`${ze('自定义告警 - 告警推送失败','Custom Alert - Push Failed')}`,
      value:NoticeRecordCategory.APP_CUSTOM_ALARM_PUSH_FAILED,
      color:'volcano',
      icon: <WarningOutlined style={{color:'#FA541C', fontSize: 14}} />,
    },{
      label:`${ze('系统告警 - 崩溃','System Alert and Crash')}`,
      value:NoticeRecordCategory.APP_PRECONFIGURED_CRASH_ALARM,
      color:'#FF5461',
      icon: <IconCrashList width="14" height="14" viewBox="0 0 20 20" style={{color:'#FFFFFF'}} />,
    },{
      label:`${ze('系统告警 - 错误','System Alert and Error')}`,
      value:NoticeRecordCategory.APP_PRECONFIGURED_ERROR_ALARM,
      color:'#A452DD',
      icon: <IconErrorList  width="14" height="14" viewBox="0 0 20 20" style={{color:'#FFFFFF'}}/>,
    },
  ];

  // 将 NoticeRecordCategory 转换为 ExceptionCategory
  const mapToExceptionCategory = (value) => {
    if (
      value === NoticeRecordCategory.APP_CUSTOM_CRASH_ALARM ||
      value === NoticeRecordCategory.APP_PRECONFIGURED_CRASH_ALARM ||
      value === NoticeRecordCategory.APP_CUSTOM_MIXED_ALARM
    ) {
      return ExceptionCategory.CRASH;
    }

    if (
      value === NoticeRecordCategory.APP_CUSTOM_ANR_ALARM
    ) {
      return ExceptionCategory.ANR;
    }

    if (
      value === NoticeRecordCategory.APP_CUSTOM_ERROR_ALARM ||
      value === NoticeRecordCategory.APP_PRECONFIGURED_ERROR_ALARM
    ) {
      return ExceptionCategory.ERROR;
    }

    if (
      value === NoticeRecordCategory.APP_CUSTOM_HANG_ALARM
    ) {
      return ExceptionCategory.HANG;
    }

    if (
      value === NoticeRecordCategory.APP_CUSTOM_OOM_ALARM
    ) {
      return ExceptionCategory.OOM;
    }

    return value;
  };

  const [ presentState, setPresentState ] = useState(true);
  const [ contextState, setContextState ] = useState('ADD');
  const [ loading, setLoading ] = useState(false);
  const [ current, setCurrent ] = useState(1);
  const [ pageSize, setPageSize ] = useState(10);
  const [ readerData, setReaderData ] = useState([]);
  const [ trigger, setTrigger ] = useState(0);
  const [ complexContentFilter, setComplexContentFilter ] = useState();
  const [ terms, setTerms ] = useState('AllExceptions');
  const openPushFailedTrigger = useZuStore((state) => state.noticeDrawer.openPushFailedTrigger);
  const lastPushFailedTriggerRef = useRef(openPushFailedTrigger);

  const enableAiCenter = isSuper && isZh(); //超管并且中文环境显示AI分析入口

  useEffect(() => {
    if (openPushFailedTrigger && openPushFailedTrigger !== lastPushFailedTriggerRef.current) {
      lastPushFailedTriggerRef.current = openPushFailedTrigger;
      setContextState('Custom');
      setTerms(NoticeRecordCategory.APP_CUSTOM_ALARM_PUSH_FAILED);
      setPresentState(true);
      setCurrent(1);
      setComplexContentFilter({
        hierarchyLevel: 'APP',
        categoryList: [NoticeRecordCategory.APP_CUSTOM_ALARM_PUSH_FAILED],
      });
    }
  }, [openPushFailedTrigger]);

  // 每5分钟轮询一次状态，只有当页面可见时才轮询
  useEffect(() => {
    const intervalId  = setInterval(() => {
      if (document.visibilityState === 'visible') {
        setTrigger(prevCount => prevCount + 1);
        setPresentState(true);
      }
    }, 300000);

    return () => {
      clearInterval(intervalId);
    }
  }, []);

  useEffect(() => {
    setCurrent(1);
  },[pageSize])
  useEffect(() => {
    setReaderData(listNotices)
  },[listNotices,total])

  useEffect(() => {
    if(appId){
      getListNotices({current,pageSize,onlyUnread:presentState,complexContentFilter});
    }
    setLoading(false);
  },[appId,trigger,current,pageSize,presentState,complexContentFilter])

  const getExceptionTypeOptions = useMemo(() => {
    const baseFilter = generateComplexContentFilter(contextState);
    const shouldShowAll = !baseFilter || baseFilter.length === 0;

    // 保留"全部异常"选项
    const allExceptionsOption = {
      value: 'AllExceptions',
      label: ze('全部异常', 'All exceptions'),
      icon: null,
      color: 'default'
    };

    const filteredOptions = shouldShowAll
      ? announcementTypeList
      : announcementTypeList.filter(item => baseFilter.includes(item.value));

    return [allExceptionsOption, ...filteredOptions];
  }, [contextState, showOomNoticeType]);

  const getUnreadState = (e) => {
    setLoading(true);
    setPresentState(!presentState);
    setCurrent(1);
  }


  const updateContext = async(data) => {
    setLoading(true);
    const { key, value} = data;
    const hierarchyLevel = value;
    const categoryList = generateComplexContentFilter(key);
    const complexContentFilter = hierarchyLevel && categoryList && {hierarchyLevel, categoryList};
    setTerms('AllExceptions');
    await sleep(1000);
    setComplexContentFilter(complexContentFilter);
    setContextState(key);
    setLoading(false);

  }

  //分页器切换
  const paginationChange = (page,pageSize) => {
    setLoading(true);
    setCurrent(page);
  }

  const paginationSizeChange = (current,size) => {
    setLoading(true);
    setCurrent(1);
    setPageSize(size);
  }

  const updateMouseEnter = (recordId) => {
    setReaderData((prevDataList) =>
      prevDataList.map((item) =>
        item.recordId === recordId ? { ...item, showEdit: true } : item
      )
    );
  }

  const updateMouseLeave = (recordId) => {
    setReaderData((prevDataList) =>
      prevDataList.map((item) =>
        item.recordId === recordId ? { ...item, showEdit: false } : item
      )
    );
  }

  const updateReadState = async(recordId,hasRead) => {
    setLoading(true);
    await getChangeReadStatus(recordId,hasRead);
    await sleep(1000);
    if(current === 1){
      getListNotices({current,pageSize,onlyUnread:presentState,complexContentFilter});
    } else {
      setCurrent(1);
    }
    setLoading(false);
  }

  const updateAllAsRead = async() => {
    setLoading(true);
    await getMarkAllAsRead();
    await sleep(1000);
    setCurrent(1);
    setPresentState(false);
    setLoading(false);

    reportEvent({
      action: EVENT_ACTIONS.CLICK,
      tp1: '一键已读',
    });
    setCurrent(1);
    if(!presentState && current === 1){
      getListNotices({current,pageSize,onlyUnread:presentState,complexContentFilter});
    }
  }

  const getLabelByValue = (value) => {
    const selectedType = announcementTypeList.find(type => type.value === value);
    return (
      <div>
        {selectedType && (
          <Tag color={selectedType.color}>
            <div style={{display:'flex',gap:'5px',alignItems:'center'}}>
              <div style={{height:'16px'}}>{selectedType.icon}</div>
              <div>{selectedType.label}</div>
            </div>
          </Tag>
        )}
      </div>
    )
  }

  const drawerDisableClose = () => {
    drawerDisable();
    setContextState('ADD');
    setTerms('AllExceptions');
    if(!presentState){
      setComplexContentFilter();
      setPresentState(true);
    } else {
      setComplexContentFilter();
      getListNotices({current,pageSize,onlyUnread:presentState});
    }
  }

  const handleExceptionTypeChange = (value) => {
    setTerms(value);
    const baseFilter = generateComplexContentFilter(contextState);
    const categoryList = value === 'AllExceptions' ? baseFilter : [value];
    setComplexContentFilter(prev => ({
      ...prev,
      categoryList,
    }))
  }

  const noticeHeadMenu = items.map(item => (
    <li
      key={item.key}
      style={{
        borderBottom: contextState === item.key ? `4px solid ${CS_STYLES.PRIMARY_COLOR}` : '',
      }}
      onClick={() => updateContext(item)}
    >
      <div
        className={contextState === item.key ? scss.navigatorChoose : scss.navigatorUnselected}
      >
        {item.label}
      </div>
    </li>
  ))

  return (
    <div>
      <Drawer
        placement='right'
        closable={false}
        onClose={drawerDisableClose}
        open={drawerOpen}
        width={540}
        bodyStyle={{
          padding: 0,
        }}
      >
        <Spin spinning={loading}>
          <div className={scss.notice}>
            <div>
              <ul class={scss.navigator}>{noticeHeadMenu}</ul>
              <div
                style={{
                  display:'flex',
                  alignItems:'center',
                  justifyContent:'space-between',
                  borderTop: '1px solid #E9E9E9',
                  borderBottom: '1px solid #E9E9E9',
                  fontSize: '14px',
                  padding:'16px 24px'}}>
                <div style={{display:'flex',gap:'10px',alignItems:'center'}}>
                  <Checkbox checked ={presentState} onChange={(e) => getUnreadState(e)}>{ze('只看未读','View only Unread')}</Checkbox>
                  <CsSelect
                    value={terms}
                    style={{ minWidth: 200 }}
                    labelRender={(option) => (  // 检查是否有类似属性
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {option.icon}
                        {option.label}
                      </div>
                    )}
                    onChange={(v) => handleExceptionTypeChange(v)}
                  >
                    {
                      getExceptionTypeOptions.map((option) => {
                        const exceptionType = mapToExceptionCategory(option.value);
                        const icon = getExceptionIconByType({exceptionType, size: 14}) || option.icon;
                        return <Option key={option.value} value={option.value}>
                          <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
                            {icon}
                            {option.label}
                          </div>
                        </Option>;
                      })
                    }
                  </CsSelect>
                </div>
                <div
                  onClick={updateAllAsRead}
                  style={{cursor: 'pointer',display:'flex',alignItems:'center',gap:'4px'}}
                  className={scss.MarkasreadNormal}
                >
                  {/* <img src={markasread_normal_icon} alt="" /> */}
                  <MarkasreadNormal />
                  <div>{ze('一键已读','Mark all as Read')}</div>
                </div>
              </div>
            </div>
            <div style={{height:'calc(100vh - 85px - 44px)',overflowX:"auto"}}>
              {readerData.length > 0 ? readerData.map((item,index) => (
                <div key={item.recordId}>
                  <hr/>
                  <div style={{ backgroundColor: item.showEdit ? '#f0f0f0' : 'transparent'}}
                    onMouseEnter={() => updateMouseEnter(item.recordId)}
                    onMouseLeave={() => updateMouseLeave(item.recordId)}
                  >
                    <div style={{display:'flex',alignItems:'center',height:'22px',padding:'16px 24px 0 0'}}>
                      <div style={{width:'24px'}}>{ !item.hasRead && <Badge status="error" style={{margin: '8px'}} size='small'/>}</div>
                      <div>{getLabelByValue(item.category)}</div>
                      <div style={{flex:'1',fontWeight:'bolder',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{isZh() ? item.titleZh : item.titleEn}</div>
                      {item.showEdit && (
                        <div style={{display:'flex', alignItems:'center'}}>
                          <Button type="text" size="small" style={{display:'flex', alignItems:'center'}} onClick={() => updateReadState(item.recordId, item.hasRead)}>
                            {item.hasRead ? <div class={scss.Read}>{<MarkasreadNormal />}<div>{ze('已读','Read')}</div></div> : <div className={scss.unread}><MarkasunreadNormal /></div>}
                          </Button>
                          {item.alertReport && enableAiCenter && <Button
                            style={{background:'linear-gradient(120deg, #CE7EF3, #6E7FFD)', color:'#fff'}}
                            size="small"
                          >
                            <Link
                              to={`/crash-reporting/ai-reports/${appId}?pid=${pid}&alertReport=${item.alertReport}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={scss.aiLink}
                            >{ze('AI分析','AI Analysis')}</Link>
                          </Button>}
                        </div>
                      )}
                    </div>
                    <div style={{margin:'16px 24px'}}>
                      <ReactMarkdown
                        remarkPlugins={[gfm]}
                        linkTarget='_blank'
                        children={isZh() ? item.contentZh : item.contentEn}
                          components={{
                            img({ node, ...props}) {
                              return <div style={{ maxWidth:'100%' }}>
                                <Image src={props.src} />
                              </div>
                            },
                            a({ node, ...props }) {
                              const handleClick = (event) => {
                                updateReadState(item.recordId, item.hasRead)
                              };
                              return <a onClick={handleClick} {...props} />;
                            },
                          }}
                        />
                    </div>
                    <ErrorBoundary fallback={<div style={{ color: 'red',fontSize:'14px' }}>
                      {ze('渲染出错，请联系管理员','Render error, please contact administrator.')}
                    </div>}>
                      <NoticeDrawerEcharts key={item.recordId} data={item.otherInfo}/>
                    </ErrorBoundary>
                    <div style={{padding:'0 0 20px 24px',color:'#AAA',fontSize:'13px'}}>{item.updateTime}</div>
                  </div>
                </div>

              )) :  <div style={{position: 'relative',width:'100%',height:'100%'}}>
                <Empty
                  description={ze('暂无数据','No messages available')}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{position:'absolute',top:'50%',left:'50%',transform: 'translate(-50%, -50%)'}}/>
              </div>}
              <div style={{display:'flex',justifyContent:'flex-end',margin:'10px 0'}}>
                <NoJumpNextPagination
                  size='small'
                  total={total}
                  current={current}
                  pageSize={pageSize}
                  onChange={(page,pageSize) => paginationChange(page,pageSize)}
                  onShowSizeChange={ (current,size) => paginationSizeChange(current,size)}
                />
              </div>
            </div>
          </div>
        </Spin>
      </Drawer>
    </div>
  );
};
export default withRouter(NoticeDrawer);
